import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { MessagesCard } from './MessagesCard';

/**
 * Interní zprávy as a button in the top-right corner — the owner's wording
 * on 30 September: "somewhere on the right up corner with a small message
 * button and notification if there is anything". The button carries the
 * count of current messages as a badge, and opens the board (the same
 * MessagesCard the dashboard used to render inline, with its form and its
 * delete) in a popover under it. The dashboard itself no longer spends a
 * row on an empty board.
 *
 * The count refreshes every minute, like the board did, so a note left by
 * the other shift lights the badge on every screen without a reload.
 */
export function MessagesBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ['messages'], queryFn: () => api.messages(), refetchInterval: 60_000 });
  const n = q.data?.length ?? 0;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="msgbell" ref={ref}>
      <button
        type="button"
        className={n > 0 ? 'bellbtn has' : 'bellbtn'}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={n > 0 ? `Interní zprávy: ${n}` : 'Interní zprávy'}
        title="Interní zprávy"
      >
        <span aria-hidden="true">✉</span>
        {n > 0 && <span className="bellbadge">{n > 99 ? '99+' : n}</span>}
      </button>
      {open && (
        <div className="msgpop" role="dialog" aria-label="Interní zprávy">
          <MessagesCard />
        </div>
      )}
    </div>
  );
}
